// Sample patients + plans for the demo experience. Nothing here is persisted.
// Privacy-first model: we ONLY store Patient ID (from the clinic's main system)
// and Initials. No names, no age, no condition. Date of Service lives on each plan.
//
// Plan sections:
//   patient    — patient's own spoken notes (raw transcript + AI summary)
//   evaluation — therapist evaluation (Assessment, Objective, Goals, History)
//   daily      — therapist daily note (Objective, Assessment, History)

export const EMPTY_PATIENT = { transcript: "", summary: "", audioUrl: null };
export const EMPTY_EVAL = {
  transcript: "",
  audioUrl: null,
  assessment: "",
  objective: "",
  goals: "",
  history: "",
};
export const EMPTY_DAILY = {
  transcript: "",
  audioUrl: null,
  objective: "",
  assessment: "",
  history: "",
};

export const SAMPLE_PATIENTS = [
  {
    id: "PT-1042",
    initials: "MK",
    plans: [
      {
        id: "plan-1042-3",
        date: "2026-02-12",
        type: "daily",
        patient: {
          transcript: "",
          summary: "",
          audioUrl: null,
        },
        evaluation: { ...EMPTY_EVAL },
        daily: {
          transcript: "",
          audioUrl: null,
          objective:
            "Active knee flexion improved to 118 degrees, extension 0 degrees. Quadriceps MMT 4/5, hamstring 4+/5. Single-leg stance tolerated 25 seconds.",
          assessment:
            "Continued progress, week 6 post-op. Strength symmetry approaching 80%. Patient tolerating dynamic loading well, no effusion noted.",
          history:
            "Reports mild stiffness on waking, resolves within 10 minutes. Returned to stationary cycling 3x/week as advised.",
        },
      },
      {
        id: "plan-1042-2",
        date: "2026-02-05",
        type: "daily",
        patient: { ...EMPTY_PATIENT },
        evaluation: { ...EMPTY_EVAL },
        daily: {
          transcript: "",
          audioUrl: null,
          objective:
            "Knee flexion 110 degrees, extension 0 degrees. Quadriceps MMT 4-/5. Mild swelling around the patella.",
          assessment:
            "Steady recovery, week 5. Beginning closed-chain progression. Cleared for stationary cycling.",
          history:
            "Patient reports improved sleep quality. Pain 2/10 at rest, 4/10 during therapy.",
        },
      },
      {
        id: "plan-1042-1",
        date: "2026-01-08",
        type: "evaluation",
        patient: {
          transcript:
            "My right knee still feels stiff and weak after the surgery. I can't fully straighten or bend it. Walking is okay on level ground but stairs are hard. I really want to get back to playing soccer this year.",
          summary:
            "Patient reports right knee stiffness and weakness post-surgery, with incomplete extension and flexion. Walking on level ground is tolerable but stairs are difficult. Patient's main goal is to return to playing soccer this year.",
          audioUrl: null,
        },
        evaluation: {
          transcript: "",
          audioUrl: null,
          assessment:
            "Post-op right ACL reconstruction (hamstring autograft, Dec 28). Moderate effusion, ROM 0–90 degrees, quad inhibition. Patient highly motivated.",
          objective:
            "Right knee circumference +1.2 cm vs left. Active flexion 90 degrees, extension 0 degrees. Quadriceps MMT 3/5, hamstring 4/5. Patellar mobility decreased superiorly.",
          goals:
            "Short-term (4 weeks): full passive extension, flexion 120 degrees, resolve effusion. Long-term (6 months): return to recreational soccer.",
          history:
            "Recreational athlete. Non-contact ACL tear playing soccer Nov 2025. No prior knee surgeries. No comorbidities.",
        },
        daily: { ...EMPTY_DAILY },
      },
    ],
  },
  {
    id: "PT-2087",
    initials: "JR",
    plans: [
      {
        id: "plan-2087-2",
        date: "2026-02-10",
        type: "daily",
        patient: { ...EMPTY_PATIENT },
        evaluation: { ...EMPTY_EVAL },
        daily: {
          transcript: "",
          audioUrl: null,
          objective:
            "Lumbar flexion to mid-shin (improved from knee level). McKenzie repeated extensions centralized symptoms. Core endurance plank 45 seconds.",
          assessment:
            "Centralization phenomenon confirms directional preference for extension. Responding well to McKenzie approach.",
          history:
            "Pain 3/10 today, down from 6/10 at baseline. Sleeping through the night. No radicular symptoms this week.",
        },
      },
      {
        id: "plan-2087-1",
        date: "2026-01-22",
        type: "evaluation",
        patient: {
          transcript:
            "I have had this lower back pain for about eight months now. It hurts most when I sit at my desk for hours. Bending forward to tie my shoes is painful. I used to run on weekends but stopped because of this.",
          summary:
            "Patient reports lower back pain of approximately 8 months duration. Pain is worst with prolonged desk sitting. Forward bending (e.g., tying shoes) is painful. Patient has discontinued weekend running due to symptoms.",
          audioUrl: null,
        },
        evaluation: {
          transcript: "",
          audioUrl: null,
          assessment:
            "Chronic non-specific mechanical lower back pain, 8-month duration. Flexion-biased symptoms with no red flags. Likely discogenic component.",
          objective:
            "Lumbar flexion limited to knee level with pain. Extension full, painless. SLR negative bilaterally. Core endurance plank 20 seconds.",
          goals:
            "Reduce pain to 2/10 within 6 weeks. Restore full lumbar flexion. Build core endurance to 60-second plank. Return to recreational running.",
          history:
            "Office worker. Insidious onset 8 months ago after prolonged sitting. Failed two prior PT episodes. No imaging done.",
        },
        daily: { ...EMPTY_DAILY },
      },
    ],
  },
  {
    id: "PT-3315",
    initials: "AP",
    plans: [
      {
        id: "plan-3315-1",
        date: "2026-02-01",
        type: "evaluation",
        patient: {
          transcript:
            "My left shoulder has been sore for about three months. It started after I increased my overhead pressing at the gym. It hurts when I reach overhead and a bit when I sleep on that side.",
          summary:
            "Patient reports left shoulder soreness of approximately 3 months duration. Onset coincides with increased overhead pressing at the gym. Pain is provoked by overhead reaching and sleeping on the affected side.",
          audioUrl: null,
        },
        evaluation: {
          transcript: "",
          audioUrl: null,
          assessment:
            "Left supraspinatus tendinopathy with secondary impingement. Hawkins-Kennedy positive, Empty Can positive. No full-thickness tear suspected.",
          objective:
            "Active shoulder flexion 140 degrees with painful arc 80–120. Abduction 110 degrees. External rotation MMT 4/5, painful. Scapular dyskinesis on return phase.",
          goals:
            "Pain-free overhead range within 8 weeks. Restore full ROM. Improve scapular control. Return to overhead lifting at gym.",
          history:
            "Recreational lifter. Gradual onset over 3 months following increased overhead pressing volume. No trauma.",
        },
        daily: { ...EMPTY_DAILY },
      },
    ],
  },
  {
    id: "PT-4429",
    initials: "SO",
    plans: [
      {
        id: "plan-4429-3",
        date: "2026-02-11",
        type: "daily",
        patient: { ...EMPTY_PATIENT },
        evaluation: { ...EMPTY_EVAL },
        daily: {
          transcript: "",
          audioUrl: null,
          objective:
            "Independent ambulation with single-point cane on level surfaces. Hip flexion 95 degrees, abduction 25 degrees. Sit-to-stand x10 in 14 seconds.",
          assessment:
            "Excellent progress 4 weeks post-op. Meeting all precautions. Ready to progress to stairs and uneven terrain.",
          history:
            "Reports minimal pain, 1/10 with activity. Sleeping well. Returned to driving last week.",
        },
      },
      {
        id: "plan-4429-2",
        date: "2026-02-01",
        type: "daily",
        patient: { ...EMPTY_PATIENT },
        evaluation: { ...EMPTY_EVAL },
        daily: {
          transcript: "",
          audioUrl: null,
          objective:
            "Ambulation with rolling walker, 50 meters with one rest. Hip flexion 85 degrees, abduction 20 degrees. Maintains posterior precautions.",
          assessment:
            "Steady recovery, week 2 post-op. Tolerating progressive weight bearing.",
          history:
            "Pain 3/10 with activity. Adhering to precautions. No signs of DVT.",
        },
      },
      {
        id: "plan-4429-1",
        date: "2026-01-15",
        type: "evaluation",
        patient: {
          transcript:
            "I had the hip replacement five days ago. It's still pretty sore but the team here has been helping me walk with the walker. My main goal is to get back to walking around my neighborhood and driving again.",
          summary:
            "Patient is 5 days post left hip replacement. Reports soreness, ambulating with rolling walker with team assistance. Patient's primary goals are to return to community ambulation and resumption of driving.",
          audioUrl: null,
        },
        evaluation: {
          transcript: "",
          audioUrl: null,
          assessment:
            "Status post left total hip arthroplasty (posterior approach, Jan 10). Standard precautions in effect. Patient motivated, supportive home environment.",
          objective:
            "Ambulation with rolling walker, 20 meters with rest breaks. Hip flexion 70 degrees, limited by precautions. Bed mobility with assistance.",
          goals:
            "Independent ambulation with single-point cane by week 6. Return to community ambulation. Discontinue precautions at week 12 per surgeon.",
          history:
            "Long-standing left hip OA, primary THA performed 5 days ago. Lives with spouse in single-level home.",
        },
        daily: { ...EMPTY_DAILY },
      },
    ],
  },
];

export function initials(patient) {
  return patient?.initials || "";
}
